import dayjs, { ConfigType } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getToken, getTokenCookie } from './auth';

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TIME_ZONE = 'UTC';

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    try {
        const encodedPayload = token.split('.')[1];
        if (!encodedPayload) {
            return null;
        }

        const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
        const json = decodeURIComponent(
            Array.from(atob(paddedBase64))
                .map(character => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
                .join('')
        );

        return JSON.parse(json);
    } catch {
        return null;
    }
};

export const getOrganizationTimeZone = (): string => {
    const token = getTokenCookie() || getToken();
    if (!token) {
        return DEFAULT_TIME_ZONE;
    }

    const payload = decodeJwtPayload(token);
    const timeZone = payload?.timezone;

    if (typeof timeZone !== 'string' || !timeZone.trim()) {
        return DEFAULT_TIME_ZONE;
    }

    try {
        new Intl.DateTimeFormat('es-EC', { timeZone }).format();
        return timeZone;
    } catch {
        return DEFAULT_TIME_ZONE;
    }
};

const asUtcInstant = (value: ConfigType) => {
    if (typeof value === 'string' && !/(Z|[+-]\d{2}:\d{2})$/i.test(value)) {
        return dayjs.utc(`${value}Z`);
    }

    return dayjs(value).utc();
};

export const formatUtcInstant = (
    value?: string | null,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }
): string => {
    if (!value) {
        return '';
    }

    const instant = asUtcInstant(value);
    if (!instant.isValid()) {
        return '';
    }

    return new Intl.DateTimeFormat('es-EC', {
        ...options,
        timeZone: getOrganizationTimeZone()
    }).format(instant.toDate());
};

export const formatUtcDate = (value?: string | null): string =>
    formatUtcInstant(value, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

export const formatCalendarDate = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
        return '';
    }

    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
};

export const getOrganizationToday = (): string =>
    dayjs().tz(getOrganizationTimeZone()).format('YYYY-MM-DD');

export const getOrganizationDateParts = (value?: string | null) => {
    const zonedDate = asUtcInstant(value || new Date()).tz(getOrganizationTimeZone());

    return {
        year: zonedDate.year(),
        month: zonedDate.month() + 1,
        day: zonedDate.date(),
        instant: zonedDate.toDate()
    };
};

export const utcInstantToOrganizationInput = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    const instant = asUtcInstant(value);
    if (!instant.isValid()) {
        return '';
    }

    return instant.tz(getOrganizationTimeZone()).format('YYYY-MM-DDTHH:mm');
};

export const organizationInputToUtc = (value?: string | null): string => {
    if (!value) {
        return new Date().toISOString();
    }

    return dayjs.tz(value, getOrganizationTimeZone()).utc().toISOString();
};

export const organizationDateRangeToUtc = (
    startDate: string,
    endDate: string
): { startUtc: string; endUtc: string } => {
    const timeZone = getOrganizationTimeZone();

    return {
        startUtc: dayjs.tz(startDate, timeZone).startOf('day').utc().toISOString(),
        endUtc: dayjs.tz(endDate, timeZone).add(1, 'day').startOf('day').utc().toISOString()
    };
};
